import { cache } from 'react';

import { isPreviewDomain } from '../preview/is-preview-domain';

import { formatSubject, SECTION_ATTRIBUTE, SUBJECT_ATTRIBUTE, type SubjectRef } from './subjects';

/**
 * Per-request switch for marking Subjects on rendered elements. Backed by
 * React `cache()`, which is request-scoped during RSC rendering, so turning
 * it on in a layout or page is seen by every server component that request
 * renders, and never by another request. Reading it, unlike `headers()`,
 * keeps a route in the Full Route Cache: the domain sits in the ISR cache
 * path, so a Preview Site's marked render and the live domain's unmarked one
 * are separate entries. (The `forced-draft.ts` holder pattern.)
 *
 * Outside an RSC render (a client component, or a test) `cache()` keeps
 * nothing, so marking is always off there.
 */
const marksHolder = cache((): { on: boolean } => ({ on: false }));

/**
 * Turns marking on for this request. For the `/tenants/:id` tree (Shared
 * Drafts and the admin's live preview, where the marks do no harm).
 *
 * The App Router renders a layout and its page concurrently, and
 * `generateMetadata` on its own, so the layout, every page route and every
 * `generateMetadata` that emits the page's document call it for themselves,
 * first thing: none can rely on another. `subject-marks-routes.test.ts`
 * fails when a page route doesn't. A route that misses it renders no marks,
 * which is safe.
 */
export function armSubjectMarks(): void {
  marksHolder().on = true;
}

/**
 * Turns marking on for this request only when it renders the tenant's
 * Preview Site, never on a live domain, where nothing reads the marks and
 * they would publish document ids. For the tenant-domains tree.
 */
export function armSubjectMarksOnPreview({
  domain,
  previewDomain,
}: {
  domain: string;
  previewDomain: string | null | undefined;
}): void {
  if (isPreviewDomain({ domain, previewDomain })) armSubjectMarks();
}

/** Whether this request marks Subjects. */
export const areSubjectMarksOn = (): boolean => marksHolder().on;

export type SubjectAttributes = { [SUBJECT_ATTRIBUTE]?: string };
export type SectionAttributes = { [SECTION_ATTRIBUTE]?: string };

/**
 * The attribute naming `subject`, to spread onto the element that shows it,
 * or nothing when this request does not mark Subjects (or there is no
 * subject). A client component cannot call this: it takes the result from
 * its server parent as a prop instead.
 */
export function subjectAttributes(subject: SubjectRef | null | undefined): SubjectAttributes {
  if (!subject || !areSubjectMarksOn()) return {};
  return { [SUBJECT_ATTRIBUTE]: formatSubject(subject) };
}

/**
 * The attribute naming a whole-page template's section (`hero`, `faq`, ...),
 * to spread onto the section's root, or nothing when marking is off.
 */
export function sectionAttributes(section: string): SectionAttributes {
  return areSubjectMarksOn() ? { [SECTION_ATTRIBUTE]: section } : {};
}
